import { Stack } from "@mui/material";
import { useAtomValue } from "jotai";
import { filter, get, omit, sortBy } from "lodash-es";
import type { FC } from "react";
import { useMemo } from "react";
import { fromISO8601_DATE } from "../../../utils/iso8601";
import { useBrandTones } from "../../brand-kit/hooks/use-brand-tones";
import type { BrandInfoSelected } from "../../brand-kit/types";
import { libraryAtom } from "../library/store";
import type { LibraryProgram, LibraryTemplate } from "../library/types";
import type { DiscoverTemplate } from "../types";
import { DiscoverCardBadge } from "./discover-card-badge";
import { DiscoverCarousel } from "./discover-carousel";

export const programNameGetter = (t: DiscoverTemplate): string | null => {
  if ("program" in t) return t.program?.name;
  else return null;
};

export const DiscoverCarousels: FC<{
  brandKitSelections?: BrandInfoSelected;
}> = ({ brandKitSelections }) => {
  const { data: brandTones, loading } = useBrandTones();
  const brandTone = useMemo(() => brandTones?.find((b) => b.is_default), [brandTones]);
  const library = useAtomValue(libraryAtom);
  const programs = [
    "DEIB",
    "Holidays",
    "Employee Advocacy",
    "Manager Coaching",
    "Health & Wellness",
    "Employee Engagement",
    "CSR",
    "Onboarding",
  ]
    .map((main_name) => library.programs.find(({ name }) => main_name === name))
    .filter((p) => !!p);
  const templates = library.programs.flatMap((p) => p.templates);
  const popular = sortBy(
    filter(templates, (template) => template.trending !== undefined && template.trending !== ""),
    (template) => template.trending?.length,
  ).reverse();
  const upcoming = sortBy(
    templates.filter((template) => {
      const now = new Date();

      const startDate = template.date ? new Date(template.date) : null;
      const endDate = template.end_date ? new Date(template.end_date) : null;

      return (
        !!(startDate && startDate > now) ||
        !!(startDate && endDate && startDate < now && endDate > now)
      );
    }),
    (template) => template.date && new Date(template.date).getTime(),
  );
  const topten = sortBy(
    filter(
      templates.filter((template) => template.top !== null),
      (template) => template.top !== null,
    ),
    (template) => template.top,
  );
  return (
    <Stack gap={4} pt={4}>
      <DiscoverCarousel
        title="Popular Communications"
        loading={loading}
        templates={popular}
        brandTone={brandTone}
        brandKitSelections={brandKitSelections}
        Badge={DiscoverCardBadge}
        subheadingGetter={programNameGetter}
      />
      <DiscoverCarousel
        title="Upcoming Communications"
        loading={loading}
        templates={upcoming}
        brandTone={brandTone}
        brandKitSelections={brandKitSelections}
        subheadingGetter={programNameGetter}
      />
      <DiscoverCarousel
        title="Top 10 Communications"
        loading={loading}
        templates={topten}
        brandTone={brandTone}
        brandKitSelections={brandKitSelections}
        Badge={DiscoverCardBadge}
        subheadingGetter={programNameGetter}
      />
      {programs.map((libraryProgram) => (
        <DiscoverCarousel
          loading={loading}
          key={libraryProgram.name}
          title={libraryProgram.name}
          brandKitSelections={brandKitSelections}
          slug={libraryProgram.slug}
          templates={sortTemplates(libraryProgram)}
          brandTone={brandTone}
          subheadingGetter={get(programSubheadingOverrides, libraryProgram.slug)}
        />
      ))}
    </Stack>
  );
};

export const programSubheadingOverrides: {
  [key: string]: (t: DiscoverTemplate) => string | null;
} = {
  holidays: (t) => (t.type === "moment" ? t.geo : null),
};

function getSortValue(template: LibraryTemplate): Date | number {
  return template.date !== null
    ? fromISO8601_DATE(template.date)
    : parseSendTime(template.recommendations.send_time);
}

const parseSendTime = (sendTime: string): number => {
  const extractedNumber = parseInt(sendTime, 10);

  return (
    extractedNumber *
    (sendTime.includes("before") ? -1 : 1) *
    (sendTime.includes("week") ? 7 : 1) *
    (sendTime.includes("month") ? 30 : 1) *
    (sendTime.includes("year") ? 365 : 1)
  );
};

const sortTemplates = (program: LibraryProgram): LibraryTemplate[] =>
  sortBy(
    program.templates
      .filter(
        ({ end_date }) =>
          !["holidays", "deib"].includes(program.slug) ||
          fromISO8601_DATE(end_date ?? "2179-04-24") >= new Date(),
      )
      .map((template): LibraryTemplate => ({
        ...template,
        program: omit(program, ["templates"]),
      }))
      .sort((a, b) => (getSortValue(a) > getSortValue(b) ? 1 : -1)),
  );
